import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    // Vérifier l'authentification au chargement
    const savedUser = localStorage.getItem('healthbridge_user');
    const savedToken = localStorage.getItem('token');
    
    if (savedUser && savedToken) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      // Marquer l'utilisateur comme en ligne
      apiService.setOnline().catch(console.error);
      setIsOnline(true);
    }
    setLoading(false);

    // Nettoyer à la déconnexion
    return () => {
      if (isOnline) {
        apiService.setOffline().catch(console.error);
      }
    };
  }, []);

  // Effet pour gérer la fermeture de l'onglet/du navigateur
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (user) {
        // Marquer comme hors ligne avant de quitter
        apiService.setOffline().catch(console.error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user]);

  const login = async (email, password) => {
    try {
      const response = await apiService.login({ email, password });
      
      if (response.success) {
        const { user: userData, token } = response.data;
        
        // Stocker le token et les données utilisateur
        localStorage.setItem('token', token);
        localStorage.setItem('healthbridge_user', JSON.stringify(userData));
        
        setUser(userData);
        
        // Marquer comme en ligne
        await apiService.setOnline();
        setIsOnline(true);
        
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return { success: false, message: error.message };
    }
  };

  const logout = async () => {
    // Marquer comme hors ligne
    if (user) {
      try {
        await apiService.setOffline();
      } catch (error) {
        console.error('Erreur lors de la mise hors ligne:', error);
      }
    }
    
    setUser(null);
    setIsOnline(false);
    localStorage.removeItem('healthbridge_user');
    localStorage.removeItem('token');
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isOnline
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
